import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/integrated-auth';
import { ChatMessageList } from './chat-message-list';
import { ChatInput } from './chat-input';
import { ChatReportModal } from './chat-report-modal';
import { MuteUserDialog } from './mute-user-dialog';
import { ChatModerationPanel } from './chat-moderation-panel';
import { PersonalMutesPanel } from './personal-mutes-panel';
import { useLeagueChat, type ChatMessage, type GifData, type TrackData } from './hooks/use-league-chat';
import { useChatModeration, type ReportCategory, type MuteType, MUTE_DURATIONS } from './hooks/use-chat-moderation';
import { useChatStatus } from './hooks/use-chat-status';
import { usePersonalMutes } from './hooks/use-personal-mutes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MessageSquareOff, Shield, Loader2, AlertTriangle } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface LeagueChatTabProps {
  leagueId: number;
  isOwner: boolean;
  chatEnabled: boolean;
  chatImagesAllowed: boolean;
  fullScreen?: boolean;
  scrollTrigger?: number; // Changes to trigger scroll to bottom
}

export function LeagueChatTab({
  leagueId,
  isOwner,
  chatEnabled,
  chatImagesAllowed,
  fullScreen = false,
  scrollTrigger,
}: LeagueChatTabProps) {
  const { user, firebaseUser } = useAuth();
  const { toast } = useToast();
  const [reportingMessage, setReportingMessage] = useState<ChatMessage | null>(null);
  const [mutingUserId, setMutingUserId] = useState<string | null>(null);
  const [mutingUsername, setMutingUsername] = useState<string>('');

  // Check if user is banned from chat platform-wide
  const { data: chatStatus, isLoading: statusLoading } = useChatStatus();

  // DEBUG: Log component render state
  console.log(`🔔 [LeagueChatTab Render] statusLoading: ${statusLoading}, chatEnabled: ${chatEnabled}, chatStatus:`, chatStatus);

  // Get current user info - IMPORTANT: Only use backend user data when available
  // to prevent race condition where firebaseUser exists but user profile hasn't loaded yet
  const currentUserId = firebaseUser?.uid || null;
  const isFullyAuthenticated = !!(currentUserId && user?.username); // Both Firebase auth AND backend user loaded
  const currentUsername = user?.username || firebaseUser?.displayName || null; // Don't fallback to 'User'
  const currentAvatarUrl = user?.avatarImageUrl || firebaseUser?.photoURL || null;

  // Chat hook
  const chatHookEnabled = chatEnabled && !chatStatus?.isBanned;
  console.log(`🔔 [LeagueChatTab] Calling useLeagueChat with enabled: ${chatHookEnabled}, leagueId: ${leagueId}`);
  const {
    messages,
    isLoading: chatLoading,
    error: chatError,
    sendMessage,
    isSending,
  } = useLeagueChat({
    leagueId,
    userId: currentUserId,
    username: currentUsername,
    avatarUrl: currentAvatarUrl,
    enabled: chatHookEnabled,
  });
  console.log(`🔔 [LeagueChatTab] useLeagueChat returned: chatLoading=${chatLoading}, chatError=${chatError}, messages=${messages.length}`);

  // Moderation hook (for league owners)
  const {
    mutes,
    mutesLoading,
    reports,
    reportsLoading,
    muteUser,
    isMuting,
    unmuteUser,
    isUnmuting,
    reportMessage,
    isReporting,
    resolveReport,
    isResolvingReport,
    isUserMuted,
    getUserMuteInfo,
  } = useChatModeration(leagueId);

  // Personal mutes hook (for all users - global mute list)
  const {
    mutedUsers: personalMutes,
    mutedUserIds: personalMutedUserIds,
    mutesLoading: personalMutesLoading,
    muteUser: personalMuteUser,
    unmuteUser: personalUnmuteUser,
    isUnmuting: isPersonalUnmuting,
  } = usePersonalMutes();

  // Check if current user is muted (by league owner)
  const currentUserMuteInfo = currentUserId ? getUserMuteInfo(currentUserId) : null;
  const isCurrentUserMuted = currentUserId ? isUserMuted(currentUserId) : false;

  // Filter messages to hide personally muted users
  const filteredMessages = useMemo(() => {
    if (personalMutedUserIds.length === 0) return messages;
    return messages.filter(msg => !personalMutedUserIds.includes(msg.userId));
  }, [messages, personalMutedUserIds]);

  // Handler for personal muting (any member can do this)
  const handlePersonalMuteUser = useCallback(
    async (userId: string, username: string) => {
      try {
        await personalMuteUser(userId);
        toast({
          title: 'User muted',
          description: `${username}'s messages will be hidden from you across all leagues.`,
        });
      } catch (error) {
        toast({
          title: 'Failed to mute user',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [personalMuteUser, toast]
  );

  // Handler for sending text messages
  const handleSendMessage = useCallback(
    async (content: string) => {
      if (isCurrentUserMuted) {
        toast({
          title: 'You are muted',
          description: currentUserMuteInfo?.expiresAt
            ? `Your mute expires ${new Date(currentUserMuteInfo.expiresAt).toLocaleString()}`
            : 'You are permanently muted in this chat',
          variant: 'destructive',
        });
        throw new Error('You are muted in this chat');
      }
      // Safety check: prevent sending if user profile not fully loaded
      if (!isFullyAuthenticated || !currentUsername) {
        throw new Error('Please wait for your profile to load');
      }
      try {
        await sendMessage({ content });
      } catch (error: any) {
        // Show user-friendly message for mute-related errors (from Firestore security rules)
        if (error?.message?.includes('muted')) {
          toast({
            title: 'You are muted',
            description: 'You cannot send messages in this chat while muted',
            variant: 'destructive',
          });
        }
        throw error;
      }
    },
    [sendMessage, isCurrentUserMuted, isFullyAuthenticated, currentUsername, currentUserMuteInfo, toast]
  );

  // Handler for uploading and sending images
  const handleImageUpload = useCallback(
    async (file: File) => {
      if (isCurrentUserMuted) {
        toast({
          title: 'Cannot send image',
          description: 'You are muted in this chat',
          variant: 'destructive',
        });
        return;
      }
      if (!isFullyAuthenticated || !currentUsername) {
        toast({
          title: 'Cannot send image',
          description: 'Please wait for your profile to load',
          variant: 'destructive',
        });
        return;
      }

      try {
        // Convert file to base64 data URL
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to backend
        const response = await apiRequest('/api/chat/upload-image', 'POST', {
          imageData: dataUrl,
          leagueId,
        });

        // Send message with image
        await sendMessage({
          content: '',
          contentType: 'image',
          imageUrl: response.imageUrl,
        });

        toast({
          title: 'Image sent',
          description: 'Your image has been shared in chat',
        });
      } catch (error: any) {
        console.error('Image upload error:', error);
        toast({
          title: 'Failed to send image',
          description: error?.message || 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [sendMessage, isCurrentUserMuted, isFullyAuthenticated, currentUsername, leagueId, toast]
  );

  // Handler for sending GIFs
  const handleSendGif = useCallback(
    async (gifData: GifData, comment?: string) => {
      if (isCurrentUserMuted) {
        toast({
          title: 'Cannot send GIF',
          description: 'You are muted in this chat',
          variant: 'destructive',
        });
        return;
      }
      if (!isFullyAuthenticated || !currentUsername) {
        toast({
          title: 'Cannot send GIF',
          description: 'Please wait for your profile to load',
          variant: 'destructive',
        });
        return;
      }

      try {
        await sendMessage({
          content: comment || '',
          contentType: 'gif',
          gifData,
        });
      } catch (error: any) {
        console.error('GIF send error:', error);
        toast({
          title: 'Failed to send GIF',
          description: error?.message || 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [sendMessage, isCurrentUserMuted, isFullyAuthenticated, currentUsername, toast]
  );

  // Handler for sending tracks
  const handleSendTrack = useCallback(
    async (trackData: TrackData, comment?: string) => {
      if (isCurrentUserMuted) {
        toast({
          title: 'Cannot share track',
          description: 'You are muted in this chat',
          variant: 'destructive',
        });
        return;
      }
      if (!isFullyAuthenticated || !currentUsername) {
        toast({
          title: 'Cannot share track',
          description: 'Please wait for your profile to load',
          variant: 'destructive',
        });
        return;
      }

      try {
        await sendMessage({
          content: comment || '',
          contentType: 'track',
          trackData,
        });
      } catch (error: any) {
        console.error('Track send error:', error);
        toast({
          title: 'Failed to share track',
          description: error?.message || 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [sendMessage, isCurrentUserMuted, isFullyAuthenticated, currentUsername, toast]
  );

  // Handler for reporting messages
  const handleReport = useCallback(
    async (category: ReportCategory, description?: string) => {
      if (!reportingMessage) return;

      // For non-text messages, include the media URL as content for the report
      let contentForReport = reportingMessage.content;
      if (!contentForReport || contentForReport.trim() === '') {
        if (reportingMessage.contentType === 'image' && reportingMessage.imageUrl) {
          contentForReport = `[Image: ${reportingMessage.imageUrl}]`;
        } else if (reportingMessage.contentType === 'gif' && reportingMessage.gifData) {
          contentForReport = `[GIF: ${reportingMessage.gifData.url}]`;
        } else if (reportingMessage.contentType === 'track' && reportingMessage.trackData) {
          contentForReport = `[Track: ${reportingMessage.trackData.title} by ${reportingMessage.trackData.artistName}]`;
        } else {
          contentForReport = `[${reportingMessage.contentType} message]`;
        }
      }

      await reportMessage({
        leagueId,
        messageId: reportingMessage.id,
        messageContent: contentForReport,
        messageAuthorId: reportingMessage.userId,
        category,
        description,
      });
    },
    [reportingMessage, reportMessage, leagueId]
  );

  // Handler for deleting messages (soft delete via backend)
  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      try {
        await apiRequest(`/api/leagues/${leagueId}/chat/messages/${messageId}`, 'DELETE');
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    },
    [leagueId]
  );

  // Handler for muting users
  const handleMuteUser = useCallback(
    async (muteType: MuteType, reason?: string) => {
      if (!mutingUserId) return;

      await muteUser({
        leagueId,
        userId: mutingUserId,
        muteType,
        reason,
      });

      toast({
        title: 'User muted',
        description: `${mutingUsername} has been muted for ${MUTE_DURATIONS[muteType].label.toLowerCase()}.`,
      });
    },
    [mutingUserId, mutingUsername, muteUser, leagueId, toast]
  );

  // Handler for unmuting users
  const handleUnmuteUser = useCallback(
    async (oderId: string) => {
      await unmuteUser({ leagueId, oderId });
    },
    [unmuteUser, leagueId]
  );

  // Handler for resolving reports
  const handleResolveReport = useCallback(
    async (reportId: string, status: 'dismissed' | 'actioned', actionTaken?: string) => {
      await resolveReport({
        leagueId,
        reportId,
        status,
        actionTaken,
      });
    },
    [resolveReport, leagueId]
  );

  // Loading state
  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Platform-wide ban
  if (chatStatus?.isBanned) {
    return (
      <Alert variant="destructive" className="m-4">
        <Shield className="h-4 w-4" />
        <AlertTitle>Chat Access Restricted</AlertTitle>
        <AlertDescription>
          Your chat access has been suspended.
          {chatStatus.banReason && <p className="mt-1">Reason: {chatStatus.banReason}</p>}
          {chatStatus.banExpiresAt && (
            <p className="mt-1">
              This restriction will be lifted on{' '}
              {new Date(chatStatus.banExpiresAt).toLocaleDateString()}.
            </p>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Chat disabled for this league
  if (!chatEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
        <MessageSquareOff className="h-16 w-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">Chat is disabled</p>
        <p className="text-sm">The league owner has disabled chat for this league.</p>
      </div>
    );
  }

  // Mute expiry message
  const muteExpiryMessage = currentUserMuteInfo?.expiresAt
    ? `Muted until ${new Date(currentUserMuteInfo.expiresAt).toLocaleString()}`
    : 'You are permanently muted';

  // Render the moderation/settings panel
  const renderSettingsPanel = () => (
    isOwner ? (
      <ChatModerationPanel
        mutes={mutes}
        reports={reports}
        mutesLoading={mutesLoading}
        reportsLoading={reportsLoading}
        onUnmute={handleUnmuteUser}
        isUnmuting={isUnmuting}
        onResolveReport={handleResolveReport}
        isResolvingReport={isResolvingReport}
        personalMutes={personalMutes}
        personalMutesLoading={personalMutesLoading}
        onPersonalUnmute={personalUnmuteUser}
        isPersonalUnmuting={isPersonalUnmuting}
      />
    ) : (
      <PersonalMutesPanel />
    )
  );

  return (
    <div className={`flex flex-col bg-background ${fullScreen ? 'h-full' : 'h-[500px] lg:h-[600px] rounded-lg border'}`}>
      {/* Header - only show in inline mode (fullScreen mode has external header) */}
      {!fullScreen && (
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">League Chat</h3>
            {messages.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {renderSettingsPanel()}
        </div>
      )}

      {/* Mute warning */}
      {isCurrentUserMuted && (
        <div className="px-4 py-2 bg-orange-500/10 border-b">
          <div className="flex items-center gap-2 text-sm text-orange-600">
            <AlertTriangle className="h-4 w-4" />
            <span>{muteExpiryMessage}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <ChatMessageList
        messages={filteredMessages}
        isLoading={chatLoading}
        error={chatError}
        currentUserId={currentUserId}
        isOwner={isOwner}
        onReport={(message) => setReportingMessage(message)}
        onDelete={handleDeleteMessage}
        onMuteUser={isOwner ? (userId, username) => {
          setMutingUserId(userId);
          setMutingUsername(username);
        } : undefined}
        onPersonalMuteUser={handlePersonalMuteUser}
        personalMutedUserIds={personalMutedUserIds}
        scrollTrigger={scrollTrigger}
      />

      {/* Input */}
      <ChatInput
        onSendMessage={handleSendMessage}
        onSendGif={handleSendGif}
        onSendTrack={handleSendTrack}
        isSending={isSending}
        disabled={isCurrentUserMuted || !isFullyAuthenticated}
        disabledReason={
          isCurrentUserMuted
            ? muteExpiryMessage
            : !currentUserId
            ? 'Sign in to chat'
            : !isFullyAuthenticated
            ? 'Loading profile...'
            : undefined
        }
        imagesAllowed={chatImagesAllowed}
        onImageUpload={chatImagesAllowed ? handleImageUpload : undefined}
        leagueId={leagueId}
      />

      {/* Report Modal */}
      <ChatReportModal
        open={!!reportingMessage}
        onOpenChange={(open) => !open && setReportingMessage(null)}
        message={reportingMessage}
        onReport={handleReport}
        isReporting={isReporting}
      />

      {/* Mute Dialog */}
      <MuteUserDialog
        open={!!mutingUserId}
        onOpenChange={(open) => !open && setMutingUserId(null)}
        username={mutingUsername}
        userId={mutingUserId || ''}
        onMute={handleMuteUser}
        isMuting={isMuting}
      />
    </div>
  );
}
